import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { EbayToken } from './amazon.service';
import { lastValueFrom, timeout } from 'rxjs';
import { EbayBrowseItemSummery } from '../models/ebay';
import { BaseHttpService } from '../auth/base-capacitor-http';
import { Preferences } from '@capacitor/preferences';
import { MatSnackBar } from '@angular/material/snack-bar';


export class EbayProductSearchSummary {
  href: string
  limit: string
  next: string
  offset: string
  prev: string
  productSummaries: ProductSummary[]
  refinement: Refinement
  total: string
}

export class ProductSummary {
  additionalImages: AdditionalImage[]
  aspects: Aspect[]
  brand: string
  ean: string[]
  epid: string
  gtin: string[]
  image: Image
  isbn: string[]
  mpn: string[]
  productHref: string
  productWebUrl: string
  title: string
  upc: string[]
}

export class AdditionalImage {
  height: string
  imageUrl: string
  width: string
}

export class Aspect {
  localizedName: string
  localizedValues: string[]
}

export class Image {
  height: string
  imageUrl: string
  width: string
}

export class Refinement {
  aspectDistributions: AspectDistribution[]
  dominantCategoryId: string
}

export class AspectDistribution {
  aspectValueDistributions: AspectValueDistribution[]
  localizedAspectName: string
}

export class AspectValueDistribution {
  localizedAspectValue: string
  matchCount: string
  refinementHref: string
}

@Injectable({
  providedIn: 'root'
})
export class EbayService extends BaseHttpService {

  private baseUrl: string = environment.server.mainURL;
  private urlEndPoint: string;
  private readonly ebayTokenKey: string = 'token';
  private readonly lastEbayTimeToken: string = 'lastRecordedTime';
  private readonly ebayData: string = 'ebayData';
  private ebayHeades: HttpHeaders = new HttpHeaders({ 'X-EBAY-C-MARKETPLACE-ID': 'EBAY_US' });

  constructor(client: HttpClient, snackBar: MatSnackBar) {
    super(client, snackBar);
  }



  private async getEbayAuthToken(): Promise<EbayToken> {
    this.urlEndPoint = this.baseUrl + 'ebay/auth';
    return this.get<EbayToken>(this.urlEndPoint);
  }



  async ebayBrowseSearch(searchType: string, search: string, condition: string, limit: number, offset: number): Promise<EbayBrowseItemSummery> {

    let ebayToken: EbayToken = await this.getLatestEbayToken();
    let headers = await this.getAuthHeaders();
    headers['Ebay-Token'] = ebayToken.access_token;
    this.urlEndPoint = this.baseUrl + 'ebay/browser-item-summery-search';

    let params = {
      searchType: searchType,
      search: search,
      condition: condition,
      limit: limit.toString(),
      offset: offset.toString()
    };
    return this.get<EbayBrowseItemSummery>(this.urlEndPoint, params, headers);
  }

  async ebayCatalogSearch(searchType: string, search: string, condition: string, limit: number, offset: number){

    let ebayToken: EbayToken = await this.getLatestEbayToken();
    let headers = await this.getAuthHeaders();
    headers['Ebay-Token'] = ebayToken.access_token;
    this.urlEndPoint = this.baseUrl + 'ebay/product-summary/search';
   
    let params = {
      searchType: searchType,
      search: search,
      condition: condition,
      limit: limit.toString(),
      offset: offset.toString()
    };

    return this.get<EbayProductSearchSummary>(this.urlEndPoint, params, headers);
  }

  getEbaySalesHistoryByGtin(gtin: string, categeryId: string) {
    const url: string = 'https://api.ebay.com/buy/marketplace-insights/v1_beta/item_sales/search';

    let params = new HttpParams()
      .set('gtin', gtin)
      .set('category_id', categeryId);

    return lastValueFrom(this.client.get(url, { headers: this.ebayHeades }).pipe(timeout(50000)));
  }

  async getLatestEbayToken(): Promise<EbayToken> {
    let token = await this.getStoredEbayToken();
    // token time limit is 2 hours
    const isExpired = await this.isEbayTokenExpired();
    if (isExpired) {
      token = await this.getEbayAuthToken();
      this.storeEbayToken(token);
    }
    return token;
  }


  private async isEbayTokenExpired(): Promise<boolean> {
    let lastRecordedTime = await Preferences.get({ key: this.lastEbayTimeToken });

    let now = new Date().getTime();
    let diff: number = now - parseInt(lastRecordedTime.value || '0');
    let timeGap: number = 15 * 60 * 1000; // 15 minutes
    let timeLimit: number = 2 * 60 * 60 * 1000; // 2 hours

    return diff >= timeLimit - timeGap;

  }

  async storeEbayData(data: EbayBrowseItemSummery) {
    await Preferences.set({ key: this.ebayData, value: JSON.stringify(data) });
  }


  async getStoredEbayData(): Promise<EbayBrowseItemSummery> {
    const { value } = await Preferences.get({ key: this.ebayData });

    try {
      return JSON.parse(value);

    } catch (err) {
      return null;
    }

  }

  async clearEbayData(): Promise<void> {
    await Preferences.remove({ key: this.ebayData });
  }

  private async clearStoredEbayToken() {
    await Preferences.remove({ key: this.ebayTokenKey });
  }

  private storeEbayToken(token: EbayToken) {

    let currentTimer: number = new Date().getTime();
    let timeGap: number = 15 * 60 * 1000; // 15 minutes
    let timeLimit: number = 2 * 60 * 60 * 1000; // 2 hours
    token.expires_in = currentTimer + timeLimit - timeGap; // adding 2 hours to time gap and removing 15 minutes 

    Preferences.set({ key: this.ebayTokenKey, value: JSON.stringify(token) });
    Preferences.set({ key: this.lastEbayTimeToken, value: new Date().getTime().toString() })

  }

  private async getStoredEbayToken(): Promise<EbayToken> {
    const token = await Preferences.get({ key: this.ebayTokenKey });
    return token ? JSON.parse(token.value) : { access_token: '', expires_in: 0, token_type: '' };
  }

}
